import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import {
	Box,
	Button,
	ButtonGroup,
	Grid2,
	Paper,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Moment } from "moment";
import { useEffect, useState } from "react";

import { ATHENS_GRAY } from "@/shared/colors";
import { PRODUCT_AREA_ASSET_ID } from "@/shared/models/Product";
import type { WeldTest } from "@/shared/models/WeldTest";
import { getWeldTests } from "@/shared/repositories/WeldTestRepository";
import { LinesFilter } from "@/pages/delays/shared/components/filters/LinesFilter";
import {
	EditWeldTestModal,
	type WeldTestModalMode,
} from "./components/EditWeldTestModal";
import { WeldTestsTable } from "./components/WeldTestsTable";

const moment = getMomentTz();

type ViewMode = "shift" | "period";

export const WeldTestsManagerPage = () => {
	const queryClient = useQueryClient();
	const [error, setError] = useState("");
	const [modal, setModal] = useState<{
		test: WeldTest;
		mode: WeldTestModalMode;
	} | null>(null);
	const [viewMode, setViewMode] = useState<ViewMode>("shift");
	const [filterLine, setFilterLine] = useState<number | null>(null);
	const [periodStart, setPeriodStart] = useState<Moment | null>(() =>
		moment().subtract(6, "days").startOf("day"),
	);
	const [periodEnd, setPeriodEnd] = useState<Moment | null>(() =>
		moment().endOf("day"),
	);
	const isPeriod = viewMode === "period";

	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const shiftAssetId = PRODUCT_AREA_ASSET_ID.weld;

	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
	useEffect(() => {
		(async () => {
			await getShiftDataFromAPI(null, 0, shiftAssetId);
		})();
	}, []);

	const shiftId = shiftInfo?.CurrentShiftId ?? null;
	const periodStartStr = periodStart?.toISOString() ?? null;
	const periodEndStr = periodEnd?.toISOString() ?? null;

	const { data: tests, isLoading } = useQuery<WeldTest[], Error>({
		queryKey: [
			"weldTests",
			filterLine,
			isPeriod ? "period" : shiftId,
			isPeriod ? periodStartStr : null,
			isPeriod ? periodEndStr : null,
		],
		queryFn: () =>
			isPeriod
				? getWeldTests({
						assetId: filterLine ?? undefined,
						startDate: periodStartStr ?? undefined,
						endDate: periodEndStr ?? undefined,
					})
				: getWeldTests({
						assetId: filterLine ?? undefined,
						shiftId: shiftId ?? undefined,
					}),
		enabled: isPeriod ? !!(periodStart && periodEnd) : shiftId !== null,
		onError: (e: Error) => setError(e.message),
		// Mutations broadcast FAB.GetWeldTests over SignalR (SYSTEM.QueryCacheInvalidations)
		meta: { realtime: ["FAB.GetWeldTests"] },
	});

	const refresh = () =>
		queryClient.invalidateQueries({ queryKey: ["weldTests"] });

	return (
		<>
			<HelmetDexteel title="Weldline Tests Manager" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				style={{ backgroundColor: ATHENS_GRAY }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Weldline Tests Manager
					</Typography>
				</Grid2>

				<Grid2 size="auto">
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<Box display="flex" alignItems="center" gap={2}>
							<ButtonGroup size="small" color="primary">
								<Button
									variant={viewMode === "shift" ? "contained" : "outlined"}
									onClick={() => setViewMode("shift")}
									sx={{ textTransform: "none", px: 2 }}
								>
									Shift
								</Button>
								<Button
									variant={viewMode === "period" ? "contained" : "outlined"}
									onClick={() => setViewMode("period")}
									sx={{ textTransform: "none", px: 2 }}
								>
									Period
								</Button>
							</ButtonGroup>
							{isPeriod ? (
								<Box display="flex" alignItems="center" gap={1}>
									<DatePicker
										label="Start"
										value={periodStart}
										onChange={(v) =>
											setPeriodStart(v ? v.startOf("day") : null)
										}
										format="MM/DD/YYYY"
										maxDate={periodEnd ?? undefined}
										slotProps={{
											textField: { size: "small", sx: { width: 150 } },
										}}
									/>
									<DatePicker
										label="End"
										value={periodEnd}
										onChange={(v) => setPeriodEnd(v ? v.endOf("day") : null)}
										format="MM/DD/YYYY"
										minDate={periodStart ?? undefined}
										slotProps={{
											textField: { size: "small", sx: { width: 150 } },
										}}
									/>
								</Box>
							) : (
								<ShiftDayNavigatorControl
									preventInit
									withPaper={false}
									assetId={shiftAssetId}
								/>
							)}
						</Box>
					</Paper>
				</Grid2>
				<Grid2 size={{ xs: "auto" }}>
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<LinesFilter
							value={filterLine}
							onChange={setFilterLine}
							areaAssetId={PRODUCT_AREA_ASSET_ID.weld}
						/>
					</Paper>
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<WeldTestsTable
						isLoading={isLoading}
						rows={tests ?? []}
						onEdit={(test) => setModal({ test, mode: "update" })}
						onDelete={(test) => setModal({ test, mode: "delete" })}
					/>
				</Grid2>
			</Grid2>

			<EditWeldTestModal
				test={modal?.test ?? null}
				mode={modal?.mode ?? ""}
				onClose={() => setModal(null)}
				onSaved={refresh}
				onError={(e) => setError(e.message)}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
